import React, { useState, useEffect } from "react";
import {
  getShortcuts,
  addShortcut,
  updateShortcut,
  deleteShortcut,
} from "../services/api";

const AdminShortcuts = () => {
  const [shortcuts, setShortcuts] = useState([]);
  const [newOriginalKey, setNewOriginalKey] = useState("");
  const [newMappedKey, setNewMappedKey] = useState("");
  const [editingShortcut, setEditingShortcut] = useState(null); // { originalKey, mappedKey }
  const [editMappedKey, setEditMappedKey] = useState("");

  useEffect(() => {
    fetchShortcuts();
  }, []);

  const fetchShortcuts = async () => {
    try {
      const response = await getShortcuts();
      setShortcuts(response.data);
    } catch (error) {
      console.error("Erro ao buscar atalhos:", error);
      alert("Erro ao carregar atalhos.");
    }
  };

  const handleAddShortcut = async () => {
    if (!newOriginalKey || !newMappedKey) {
      alert("Por favor, preencha ambos os campos para o novo atalho.");
      return;
    }
    try {
      const response = await addShortcut({
        originalKey: newOriginalKey,
        mappedKey: newMappedKey,
      });
      setShortcuts(response.data);
      setNewOriginalKey("");
      setNewMappedKey("");
    } catch (error) {
      console.error("Erro ao adicionar atalho:", error);
      alert(error.response?.data?.message || "Erro ao adicionar atalho.");
    }
  };

  const startEdit = (shortcut) => {
    setEditingShortcut(shortcut);
    setEditMappedKey(shortcut.mappedKey);
  };

  const cancelEdit = () => {
    setEditingShortcut(null);
    setEditMappedKey("");
  };

  const handleUpdateShortcut = async (originalKey) => {
    if (!editMappedKey) {
      alert("Por favor, preencha a nova tecla mapeada.");
      return;
    }
    try {
      const response = await updateShortcut(originalKey, {
        mappedKey: editMappedKey,
      });
      setShortcuts(response.data);
      cancelEdit();
    } catch (error) {
      console.error("Erro ao atualizar atalho:", error);
      alert(error.response?.data?.message || "Erro ao atualizar atalho.");
    }
  };

  const handleDeleteShortcut = async (originalKey) => {
    if (window.confirm("Tem certeza que deseja excluir este atalho?")) {
      try {
        const response = await deleteShortcut(originalKey);
        setShortcuts(response.data);
      } catch (error) {
        console.error("Erro ao deletar atalho:", error);
        alert(error.response?.data?.message || "Erro ao deletar atalho.");
      }
    }
  };

  return (
    <div className="container mx-auto p-4 mt-12">
      <h1 className="text-2xl font-bold mb-6 text-center text-gray-800">
        Gerenciar Atalhos de Teclado
      </h1>

      {/* Adicionar Novo Atalho */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">
          Adicionar Novo Atalho
        </h2>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[150px]">
            <label
              htmlFor="newOriginalKey"
              className="block text-sm font-medium text-gray-700"
            >
              Tecla Original
            </label>
            <input
              type="text"
              id="newOriginalKey"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={newOriginalKey}
              onChange={(e) => setNewOriginalKey(e.target.value)}
              placeholder="Ex: Ctrl+S"
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <label
              htmlFor="newMappedKey"
              className="block text-sm font-medium text-gray-700"
            >
              Tecla Mapeada (para BD)
            </label>
            <input
              type="text"
              id="newMappedKey"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={newMappedKey}
              onChange={(e) => setNewMappedKey(e.target.value)}
              placeholder="Ex: *H#"
            />
          </div>
          <button
            onClick={handleAddShortcut}
            className="ml-auto px-6 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Adicionar Atalho
          </button>
        </div>
      </div>

      {/* Lista de Atalhos Cadastrados */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">
          Atalhos Cadastrados
        </h2>
        {shortcuts.length === 0 ? (
          <p className="text-gray-500">Nenhum atalho cadastrado ainda.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {shortcuts.map((shortcut) => (
              <li
                key={shortcut.originalKey}
                className="py-4 flex flex-col md:flex-row items-start md:items-center justify-between"
              >
                {editingShortcut?.originalKey === shortcut.originalKey ? (
                  // Modo de Edição
                  <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Original:
                      </p>
                      <p className="text-md text-gray-700 font-bold">
                        {shortcut.originalKey}
                      </p>
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor={`editMappedKey-${shortcut.originalKey}`}
                        className="block text-sm font-medium text-gray-700"
                      >
                        Mapeada:
                      </label>
                      <input
                        type="text"
                        id={`editMappedKey-${shortcut.originalKey}`}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={editMappedKey}
                        onChange={(e) => setEditMappedKey(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2 mt-4 md:mt-0">
                      <button
                        onClick={() =>
                          handleUpdateShortcut(shortcut.originalKey)
                        }
                        className="px-4 py-2 bg-green-600 text-white font-semibold rounded-md shadow-sm hover:bg-green-700"
                      >
                        Salvar
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 bg-gray-300 text-gray-700 font-semibold rounded-md shadow-sm hover:bg-gray-400"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  // Modo de Visualização
                  <div className="flex flex-col md:flex-row items-start md:items-center w-full justify-between">
                    <div className="flex-1 mb-2 md:mb-0">
                      <p className="text-sm font-medium text-gray-900">
                        Tecla Original:
                      </p>
                      <p className="text-md text-gray-700 font-bold">
                        {shortcut.originalKey}
                      </p>
                    </div>
                    <div className="flex-1 mb-2 md:mb-0">
                      <p className="text-sm font-medium text-gray-900">
                        Tecla Mapeada:
                      </p>
                      <p className="text-md text-gray-700">
                        {shortcut.mappedKey}
                      </p>
                    </div>
                    <div className="flex gap-2 mt-4 md:mt-0">
                      <button
                        onClick={() => startEdit(shortcut)}
                        className="px-4 py-2 bg-yellow-500 text-white font-semibold rounded-md shadow-sm hover:bg-yellow-600"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteShortcut(shortcut.originalKey)
                        }
                        className="px-4 py-2 bg-red-600 text-white font-semibold rounded-md shadow-sm hover:bg-red-700"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminShortcuts;
